import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { IconType } from '@shared/enums';
import { TimelineItem } from './timeline-item.model';

export type TimelineState = 'completed' | 'current' | 'pending' | 'skipped';

@Component({
  selector: 'app-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
})
export class TimelineComponent implements OnInit, OnChanges {
  // Optional per-item content override — when a consumer projects an <ng-template> as this
  // component's content, it replaces the built-in label/value/date/description block entirely
  // (the bullet + connector line stay exactly as-is). Context: { $implicit: item.item (the raw
  // list entry), index }. Existing consumers that project nothing are unaffected.
  @ContentChild(TemplateRef) itemTemplate?: TemplateRef<{ $implicit: any; index: number }>;

  @Input() list: TimelineItem[];
  @Input() actions: { id: string; label: string }[];
  @Input() translationPrefix: string;
 
  @Input() valuePath = 'value';
  @Input() valuePrefix = '';
  
  @Input() labelPath = 'label';
  @Input() labelBold = false;
  /** Optional content below each row; receives the original item and its list position. */
  @Input() itemContentTemplate?: TemplateRef<{ $implicit: TimelineItem; index: number; last: boolean }>;
  
  @Input() datePath = 'createdAt';
  
  @Input() descriptionPath = 'description';
  
  @Input() statePath = 'state';
  
  @Input() bulletStyle: 'ring' | 'fill' = 'ring';

  @Input() handleEmptyRequestState = false;
  @Input() emptyRequestActions: { id: string; label: string }[];
  @Output() actionClicked = new EventEmitter();
  displayedList: any[];

  constructor() {}

  ngOnInit(): void {
    this.generateDisplayedList();
  }

  ngOnChanges({ list }: SimpleChanges): void {
    if (list && list.currentValue !== list.previousValue) {
      this.generateDisplayedList();
    }
  }

  /**
   * Track by the original list entry. `generateDisplayedList()` rebuilds every wrapper object each
   * time `list` changes, so without this the whole list — including any component projected through
   * `itemTemplate` — would be destroyed and recreated, losing its internal state (form touched
   * flags, focus) on every refresh.
   */
  trackByEntry = (_: number, entry: { item: TimelineItem }) => entry.item;

  getValueFromPath(obj, path): any {
    const keys = path.split('.');
    return keys.reduce((group, key) => {
      return group[key];
    }, obj);
  }

  handleActionClick(item, action): void {
    this.actionClicked.emit({ item: item.item, action: action.id });
  }

  private buildDescriptionLines(item: TimelineItem): { text: string; color: string }[] {
    if (!this.descriptionPath) {
      return [];
    }
    const raw = this.getValueFromPath(item, this.descriptionPath);
    const defaultColor = item.descriptionColor;
    const entries = Array.isArray(raw) ? raw : raw ? [raw] : [];

    return entries
      .map((entry) => (typeof entry === 'string' ? { text: entry, color: defaultColor } : { text: entry?.text, color: entry?.color ?? defaultColor }))
      .filter((line) => !!line.text);
  }

  generateDisplayedList(): void {
    this.displayedList = (this.list || []).map((item: TimelineItem) => {
      return {
        item,
        value: this.getValueFromPath(item, this.valuePath),
        valuePrefix: this.valuePrefix,
        label: this.getValueFromPath(item, this.labelPath),
        date: this.getValueFromPath(item, this.datePath),
        descriptionLines: this.buildDescriptionLines(item),
        state: this.statePath ? (this.getValueFromPath(item, this.statePath) as TimelineState) : null,
        danger: item.danger,
        labelBold: item.labelBold,
        contentIcon:      item.contentIcon,
        contentIconType:  item.contentIconType || IconType.SVG,
        contentIconColor: item.contentIconColor,
        contentIconSize:  item.contentIconSize || 'xs',
        bulletIcon:       item.bulletIcon,
        bulletIconType:   item.bulletIconType || IconType.MaterialDesign ,
        bulletIconColor:  item.bulletIconColor,
        bulletIconSize:   item.bulletIconSize || 'sm',
        valueColor: item.valueColor ,
        labelColor: item.labelColor
      };
    });
  }
}
